<template>
  <SectionPageShell
    section="websites"
    :title="websiteTitle || 'Website-Statistik'"
    :subtitle="websiteTitle ? 'Statistik' : null"
    show-back-link
  >
    <div v-if="websiteLink" class="mb-3">
      <a :href="websiteLink" target="_blank" rel="noopener noreferrer" class="small text-break">
        {{ websiteLink }}
      </a>
    </div>
    <div class="row">
        <div class="col-12 col-xl-8">
            <div class="card shadow-sm mb-4">
                <div class="card-body">
                    <div class="row g-3 align-items-end">
                        <div class="col-12 col-lg-8">
                            <label for="website-stats-range" class="form-label">Zeitraum</label>
                            <DaterangePicker id="website-stats-range" v-model="dateRange" placeholder="Zeitraum auswählen" />
                        </div>
                        <div class="col-12 col-lg-4">
                            <label for="website-stats-interval" class="form-label">Intervall</label>
                            <select id="website-stats-interval" v-model="statsStore.interval" class="form-control">
                                <option v-for="option in intervalOptions" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card shadow-sm mb-4">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h3 class="h5 mb-0">Aufrufe im Zeitverlauf</h3>
                    <small class="text-muted">{{ dateLabel }}</small>
                </div>
                <div class="card-body">
                    <div v-if="statsStore.loading" class="text-muted">Lade Statistik...</div>
                    <div v-else-if="!statsData" class="text-muted">Keine Daten verfügbar.</div>
                    <StatsTimeSeriesChart
                        v-else
                        :buckets="histogramBuckets"
                        :interval="statsStore.interval"
                        series-name="Aufrufe"
                    />
                </div>
            </div>
        </div>
        <div class="col-12 col-xl-4">
            <div class="card shadow-sm mb-4">
                <div class="card-header">
                    <h3 class="h5 mb-0">Überblick</h3>
                </div>
                <div class="card-body">
                    <div class="stat-highlight">
                        <small class="text-uppercase text-muted d-block">Eindeutige Aufrufe</small>
                        <strong>{{ totalUniqueHits.toLocaleString() }}</strong>
                    </div>
                    <div class="stat-highlight mt-3">
                        <small class="text-uppercase text-muted d-block">Ø eindeutige Aufrufe pro {{ intervalLabel }}</small>
                        <strong>{{ averageUniqueHitsPerPeriod.toLocaleString(undefined, {maximumFractionDigits: 1}) }}</strong>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-12">
            <div class="card shadow-sm mb-4">
                <div class="card-header">
                    <h3 class="h5 mb-0">Top Seiten</h3>
                </div>
                <div class="card-body">
                    <DataTableHistogram
                        v-if="urlTableData"
                        :columns="urlColumns"
                        :tableData="urlTableData"
                        :enableSearch="false"
                        :enableSize="false"
                        :extraHeader="false"
                        :showRowNumbers="true"
                    />
                </div>
            </div>
        </div>
    </div>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import {useWebsitesStore} from "@/stores/websites";
import {useStatsStore} from "@/stores/stats";
import DaterangePicker from "@/components/DaterangePicker.vue";
import DataTableHistogram from "@/components/DataTableHistogram.vue";
import StatsTimeSeriesChart from "@/components/stats/StatsTimeSeriesChart.vue";
import dayjs from "dayjs";

export default {
    name: "WebsitesStats",
    components: {
        DaterangePicker,
        DataTableHistogram,
        SectionPageShell,
        StatsTimeSeriesChart,
    },
    setup() {
        return {
            websiteStore: useWebsitesStore(),
            statsStore: useStatsStore(),
        }
    },
    data() {
        return {
            website: null,
            statsData: null,
            intervalOptions: [
                {label: 'Tag', value: '1d'},
                {label: 'Woche', value: '1w'},
                {label: 'Monat', value: '1M'},
                {label: 'Quartal', value: '1q'},
                {label: 'Jahr', value: '1y'},
            ],
        }
    },
    created() {
        this.loadWebsite().then(() => {
            this.loadStats()
        })
    },
    watch: {
        'statsStore.interval'() {
            this.loadStats()
        }
    },
    computed: {
        dateRange: {
            get() {
                return {
                    from: this.statsStore.from,
                    to: this.statsStore.to,
                }
            },
            set(value) {
                if (!value?.from?.dateInstance || !value?.to?.dateInstance) {
                    return
                }
                this.statsStore.from = dayjs(value.from.dateInstance).valueOf()
                this.statsStore.to = dayjs(value.to.dateInstance).valueOf()
                this.loadStats()
            }
        },
        histogramBuckets() {
            return this.statsData?.aggregations?.histogram?.buckets || []
        },
        websiteTitle() {
            return this.website?.product_type ? `Website (${this.website.product_type})` : `Website #${this.$route.params.website_id}`
        },
        websiteLink() {
            return this.website?.url || null
        },
        totalUniqueHits() {
            return this.urlBuckets.reduce((sum, bucket) => sum + (bucket.unique_hashes?.value || 0), 0)
        },
        averageUniqueHitsPerPeriod() {
            if (!this.histogramBuckets.length) {
                return 0
            }
            return this.totalUniqueHits / this.histogramBuckets.length
        },
        urlBuckets() {
            return this.statsData?.aggregations?.urls?.buckets || []
        },
        intervalLabel() {
            const labels = {
                '1d': 'Tag',
                '1w': 'Woche',
                '1M': 'Monat',
                '1q': 'Quartal',
                '1y': 'Jahr',
            }
            return labels[this.statsStore.interval] || 'Zeitraum'
        },
        dateLabel() {
            return `${dayjs(this.statsStore.from).format('DD.MM.YYYY')} bis ${dayjs(this.statsStore.to).format('DD.MM.YYYY')}`
        },
        urlColumns() {
            return [
                {
                    label: 'Seite',
                    field: 'key',
                },
                {
                    label: 'Aufrufe',
                    field: 'value',
                },
                {
                    label: 'Eindeutig',
                    field: 'unique',
                },
            ]
        },
        urlTableData() {
            const rows = this.urlBuckets.map((bucket) => ({
                key: String(bucket.key),
                data: {
                    value: bucket.doc_count,
                    unique: bucket.unique_hashes?.value || 0,
                }
            }))

            return {
                rows,
                value: {
                    total: rows.reduce((sum, row) => sum + (row.data?.value || 0), 0),
                    compareTotal: 0,
                },
                unique: {
                    total: rows.reduce((sum, row) => sum + (row.data?.unique || 0), 0),
                    compareTotal: 0,
                }
            }
        },
    },
    methods: {
        loadWebsite() {
            const existingWebsite = this.websiteStore.getWebsiteById(this.$route.params.website_id)
            if (existingWebsite) {
                this.website = existingWebsite
                return Promise.resolve(existingWebsite)
            }
            return this.websiteStore.index().then(() => {
                this.website = this.websiteStore.getWebsiteById(this.$route.params.website_id) || null
                return this.website
            })
        },
        loadStats() {
            if (!this.website?.product_config_id) {
                return
            }
            const productIds = [
                this.website.product_config_id,
                ...(this.website.predecessor_product_config_ids || []),
            ].join(',')
            this.statsStore.fetchData('page_loads', {
                from: this.statsStore.from,
                to: this.statsStore.to,
                interval: this.statsStore.interval,
                products: productIds,
            }).then(({data}) => {
                this.statsData = data
            })
        },
    },
}
</script>

<style scoped lang="scss">
.stat-highlight strong {
    font-size: 2rem;
    line-height: 1;
}
</style>
